/**
 * useBodyScrollLock — ref-counted body scroll lock.
 *
 * Fixes the "stuck page" bug on Android after closing modals:
 *   - When two modals opened sequentially each cached the current
 *     `document.body.style.overflow` and restored it on close, the
 *     second close restored "hidden" (the state captured while the
 *     first modal was still open) and the body remained locked.
 *
 * This hook keeps a single module-level counter so the body is
 * unlocked only when the LAST active modal closes, and the pre-lock
 * value is captured only once. On iOS it also preserves scroll
 * position so closing the modal does not jump to the top.
 */
import { useEffect } from "react";

let lockCount = 0;
let prevBodyOverflow = "";
let prevBodyPosition = "";
let prevBodyTop = "";
let prevBodyWidth = "";
let savedScrollY = 0;

function lock() {
  lockCount += 1;
  if (lockCount > 1) return;
  const body = document.body;
  prevBodyOverflow = body.style.overflow;
  prevBodyPosition = body.style.position;
  prevBodyTop = body.style.top;
  prevBodyWidth = body.style.width;
  savedScrollY = window.scrollY || 0;
  body.style.overflow = "hidden";
  // iOS Safari needs position:fixed to actually block the body;
  // without this the body still rubber-band-scrolls under the modal.
  const isTouch = "ontouchstart" in window;
  if (isTouch) {
    body.style.position = "fixed";
    body.style.top = `-${savedScrollY}px`;
    body.style.width = "100%";
  }
}

function unlock() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;
  const body = document.body;
  body.style.overflow = prevBodyOverflow;
  body.style.position = prevBodyPosition;
  body.style.top = prevBodyTop;
  body.style.width = prevBodyWidth;
  // Restore scroll position (iOS fix).
  if (savedScrollY) {
    window.scrollTo(0, savedScrollY);
    savedScrollY = 0;
  }
}

export default function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    lock();
    return unlock;
  }, [active]);
}

// Emergency escape hatch — call from the dev console or a bug-report
// handler if the body ever remains locked due to an unmounted component
// that forgot to call its cleanup.
export function forceUnlockBody() {
  lockCount = 0;
  const body = document.body;
  body.style.overflow = "";
  body.style.position = "";
  body.style.top = "";
  body.style.width = "";
}

if (typeof window !== "undefined") {
  window.__kalki_force_unlock_body = forceUnlockBody;
}
